import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { clientApiService, ProjectDetail } from "../../services/client-api.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  planning: { label: "Planificación", className: "bg-purple-100 text-purple-700" },
  in_progress: { label: "En progreso", className: "bg-blue-100 text-blue-700" },
  on_hold: { label: "En espera", className: "bg-yellow-100 text-yellow-700" },
  completed: { label: "Completado", className: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", className: "bg-red-100 text-red-700" },
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazado", className: "bg-red-100 text-red-700" },
};

const TYPE_LABELS: Record<string, string> = {
  residential: "Residencial",
  commercial: "Comercial",
  industrial: "Industrial",
  remodelation: "Remodelación",
  other: "Otro",
};

type Tab = "resumen" | "avances" | "documentos" | "cambios";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "resumen", label: "Resumen", icon: "📊" },
  { id: "avances", label: "Avances", icon: "📈" },
  { id: "documentos", label: "Documentos", icon: "📄" },
  { id: "cambios", label: "Cambios", icon: "🔄" },
];

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(t: number | null) {
  if (!t) return "—";
  return new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("resumen");

  useEffect(() => {
    if (!id) return;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await clientApiService.getProject(id);
        setDetail(data);
      } catch (err: any) {
        setError(err?.message || "Error al cargar proyecto");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
        {error || "Proyecto no encontrado"}
        <Link to="/dashboard/projects" className="ml-3 underline font-medium">
          ← Volver a proyectos
        </Link>
      </div>
    );
  }

  const { project, financials, recentAdvances, recentDocuments, recentChanges } = detail;
  const status = STATUS_LABELS[project.status] || {
    label: project.status,
    className: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div>
        <Link
          to="/dashboard/projects"
          className="text-sm text-slate-500 hover:text-primary inline-flex items-center gap-1 mb-3"
        >
          ← Volver a proyectos
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Cover */}
          <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 relative">
            {project.coverImageUrl ? (
              <img
                src={project.coverImageUrl}
                alt={project.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-8xl opacity-20">
                🏗️
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
                    {status.label}
                  </span>
                  <span className="text-xs text-slate-500">
                    {TYPE_LABELS[project.projectType] || project.projectType}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
                {project.address && (
                  <p className="text-sm text-slate-500 mt-1">📍 {project.address}</p>
                )}
                {project.description && (
                  <p className="text-sm text-slate-600 mt-3 max-w-3xl">{project.description}</p>
                )}
              </div>

              {/* Arquitecto */}
              {project.projectLeadName && (
                <div className="bg-slate-50 rounded-xl p-4 min-w-[220px]">
                  <p className="text-xs text-slate-500 mb-1">Arquitecto líder</p>
                  <p className="font-semibold text-slate-900">{project.projectLeadName}</p>
                  {project.projectLeadEmail && (
                    <a
                      href={`mailto:${project.projectLeadEmail}?subject=Consulta sobre proyecto: ${encodeURIComponent(project.name)}`}
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary hover:underline font-medium"
                    >
                      ✉️ Enviar correo
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-500">Avance general</span>
                <span className="font-bold text-primary text-lg">{project.progress}%</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min(project.progress, 100)}%` }}
                />
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
              <KpiBlock label="Presupuesto" value={formatCurrency(project.budget)} icon="💰" />
              <KpiBlock label="Facturado" value={formatCurrency(financials.totalInvoiced)} icon="🧾" />
              <KpiBlock label="Pagado" value={formatCurrency(financials.totalPaid)} icon="✅" />
              <KpiBlock
                label="Pendiente"
                value={formatCurrency(financials.pendingAmount)}
                icon="⏳"
                highlight={financials.pendingAmount > 0}
              />
            </div>

            {/* Fechas */}
            <div className="flex items-center gap-6 mt-6 pt-4 border-t border-slate-100 text-sm text-slate-500">
              <span>📅 Inicio: {formatDate(project.startDate)}</span>
              <span>🏁 Fin: {formatDate(project.endDate)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <span className="mr-1.5">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === "resumen" && <TabResumen detail={detail} />}
          {activeTab === "avances" && <TabAvances advances={recentAdvances} />}
          {activeTab === "documentos" && <TabDocumentos documents={recentDocuments} />}
          {activeTab === "cambios" && <TabCambios changes={recentChanges} />}
        </div>
      </div>
    </div>
  );
}

function KpiBlock({
  label,
  value,
  icon,
  highlight = false,
}: {
  label: string;
  value: string;
  icon: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-slate-50 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className={`text-lg font-bold ${highlight ? "text-primary" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}

function TabResumen({ detail }: { detail: ProjectDetail }) {
  const { recentAdvances, recentDocuments, recentChanges } = detail;

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-bold text-slate-900 mb-3 text-sm">Últimos avances</h3>
        {recentAdvances.length === 0 ? (
          <p className="text-sm text-slate-500">Sin avances registrados aún.</p>
        ) : (
          <ul className="space-y-2">
            {recentAdvances.slice(0, 3).map((a) => (
              <li key={a.id} className="flex items-center gap-3 text-sm">
                <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded shrink-0">
                  S{a.weekNumber}/{a.year}
                </span>
                <span className="text-slate-500 truncate">{a.description || "Sin descripción"}</span>
                <span className="ml-auto font-bold text-primary">+{a.progressPercentage}%</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="pt-4 border-t border-slate-100">
        <h3 className="font-bold text-slate-900 mb-3 text-sm">Documentos recientes</h3>
        {recentDocuments.length === 0 ? (
          <p className="text-sm text-slate-500">Sin documentos cargados aún.</p>
        ) : (
          <ul className="space-y-2">
            {recentDocuments.slice(0, 3).map((d) => (
              <li key={d.id} className="flex items-center gap-3 text-sm">
                <span className="text-lg">📄</span>
                <span className="truncate">{d.name}</span>
                <span className="ml-auto text-xs text-slate-400">{formatFileSize(d.size)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="pt-4 border-t border-slate-100">
        <h3 className="font-bold text-slate-900 mb-3 text-sm">Cambios recientes</h3>
        {recentChanges.length === 0 ? (
          <p className="text-sm text-slate-500">Sin órdenes de cambio.</p>
        ) : (
          <ul className="space-y-2">
            {recentChanges.slice(0, 3).map((c) => (
              <li key={c.id} className="flex items-center gap-3 text-sm">
                <span className="text-slate-500 truncate flex-1">{c.description}</span>
                <span className="font-bold text-slate-700">{formatCurrency(c.costImpact)}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    STATUS_LABELS[c.status]?.className || "bg-slate-100 text-slate-700"
                  }`}
                >
                  {STATUS_LABELS[c.status]?.label || c.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TabAvances({ advances }: { advances: ProjectDetail["recentAdvances"] }) {
  if (advances.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3 opacity-40">📈</div>
        <p className="text-slate-600 font-medium">Sin avances registrados</p>
        <p className="text-sm text-slate-400 mt-1">
          Aquí verás los reportes semanales de avance de tu obra.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {advances.map((a) => (
        <li key={a.id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="shrink-0 text-center">
            <div className="text-xs text-slate-500 font-medium">Semana</div>
            <div className="text-xl font-bold text-slate-900">{a.weekNumber}</div>
            <div className="text-xs text-slate-400">{a.year}</div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700">
              {a.description || "Sin descripción"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Registrado: {formatDate(a.createdAt)}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-lg font-bold text-primary">+{a.progressPercentage}%</div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                STATUS_LABELS[a.status]?.className || "bg-slate-100 text-slate-700"
              }`}
            >
              {STATUS_LABELS[a.status]?.label || a.status}
            </span>
          </div>
        </li>
      ))}
      
    </ul>
  );
}

function TabDocumentos({ documents }: { documents: ProjectDetail["recentDocuments"] }) {
  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3 opacity-40">📄</div>
        <p className="text-slate-600 font-medium">Sin documentos</p>
        <p className="text-sm text-slate-400 mt-1">
          Aquí verás los planos, contratos y permisos de tu proyecto.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-100">
      {documents.map((d) => (
        <li key={d.id} className="flex items-center gap-3 py-3">
          <span className="text-2xl">📄</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
            <p className="text-xs text-slate-500">
              {formatFileSize(d.size)} • Subido: {formatDate(d.createdAt)}
            </p>
          </div>
          {d.signed && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              ✅ Firmado
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function TabCambios({ changes }: { changes: ProjectDetail["recentChanges"] }) {
  if (changes.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3 opacity-40">🔄</div>
        <p className="text-slate-600 font-medium">Sin cambios registrados</p>
        <p className="text-sm text-slate-400 mt-1">
          Las modificaciones de obra y su impacto económico aparecerán aquí.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {changes.map((c) => (
        <li key={c.id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
          <span className="text-xl">🔄</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700">{c.description}</p>
            <p className="text-xs text-slate-400 mt-1">Solicitado: {formatDate(c.createdAt)}</p>
          </div>
          <div className="text-right shrink-0">
            <p className={`font-bold ${c.costImpact >= 0 ? "text-slate-900" : "text-green-600"}`}>
              {c.costImpact >= 0 ? "+" : ""}
              {formatCurrency(c.costImpact)}
            </p>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                STATUS_LABELS[c.status]?.className || "bg-slate-100 text-slate-700"
              }`}
            >
              {STATUS_LABELS[c.status]?.label || c.status}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}